import {isEmpty} from 'lodash'
import * as z from 'zod/mini'
import * as fuels from '../../constants/fuels'
import * as legalForms from '../../constants/legalFormTypes'
import {PRIVATE_PERSON} from '../../constants/legalFormTypes'
import {DISCOUNT_UZP} from '../../constants/unionV2/discounts'
import * as vehicleTypes from '../../constants/vehicleTypes'
import * as common from './common'
import * as refine from './refine'
import * as superRefine from './superRefine'
import * as types from './types'
import {discounts as unionV2Discounts} from './unionV2'


const scooterConfig = (): boolean => {
  if (typeof __isClientOnly === 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const backendConfig = require('../../backend/config').default
    return backendConfig?.vehicleType?.scooter
  }

  // eslint-disable-next-line no-process-env
  return process.env.vehicle_type_scooter === 'true'
}

export const vehicle = z.discriminatedUnion('vehicleType', [
  z.object({
    vehicleType: common.vehicleTypeCar(),
    licensePlate: z.optional(common.licensePlate()),
    vin: z.optional(common.vin()),
    brand: common.vehicle.brand,
    model: common.vehicle.model,
    weight: common.vehicle.weight,
    volume: common.vehicle.volume,
    power: common.vehicle.power,
    fuel: common.vehicle.fuel,
    seats: common.vehicle.seats,
    productionYear: common.vehicle.productionYear,
  }),
  z.object({
    vehicleType: z.literal(vehicleTypes.VEHICLE_TYPE_SCOOTER),
    licensePlate: types.undefinedOptional(),
    vin: z.optional(common.scooterSerialNumber()),
    brand: types.undefinedOptional(),
    model: types.undefinedOptional(),
    weight: common.vehicle.weight,
    volume: types.undefinedOptional(),
    power: common.vehicle.power,
    fuel: z.literal(fuels.FUEL_ELEKTRO),
    seats: types.nonNegativeInteger({max: 1}),
    productionYear: common.vehicle.productionYear,
  }),
]).check(
  z.superRefine(superRefine.volumeForNonElectroFuel),
  z.superRefine(({vehicleType}, ctx) => {
    if (vehicleType === vehicleTypes.VEHICLE_TYPE_SCOOTER && !scooterConfig()) {
      ctx.addIssue({
        code: 'custom',
        path: ['vehicleType'],
        message: 'Vehicle type scooter is currently turned OFF.',
      })
    }
    return z.NEVER
  }),
)

export type Vehicle = z.infer<typeof vehicle>

export const extendedVehicle = z.discriminatedUnion('vehicleType', [
  z.object({
    vehicleType: common.extendedVehicleTypeCar(),
    licensePlate: z.optional(common.licensePlate()),
    vin: z.optional(common.vin()),
    brand: common.vehicle.brand,
    model: common.vehicle.model,
    weight: common.vehicle.weight,
    volume: common.vehicle.volume,
    power: common.vehicle.power,
    fuel: common.extendedFuel(),
    fuelOther: z.optional(types.standardString()),
    seats: common.vehicle.seats,
    productionYear: common.vehicle.productionYear,
  }),
  z.object({
    vehicleType: z.literal(vehicleTypes.VEHICLE_TYPE_SCOOTER),
    licensePlate: types.undefinedOptional(),
    vin: z.optional(common.scooterSerialNumber()),
    brand: types.undefinedOptional(),
    model: types.undefinedOptional(),
    weight: common.vehicle.weight,
    volume: types.undefinedOptional(),
    power: common.vehicle.power,
    fuel: z.literal(fuels.FUEL_ELEKTRO),
    fuelOther: z.optional(types.standardString()),
    seats: types.nonNegativeInteger({max: 1}),
    productionYear: common.vehicle.productionYear,
  }),
]).check(
  z.superRefine(superRefine.volumeForNonElectroFuel),
  z.superRefine(({fuel, fuelOther}, ctx) => {
    if (fuel === fuels.FUEL_OTHER && isEmpty(fuelOther)) {
      ctx.addIssue({
        code: 'custom',
        path: ['fuelOther'],
        message: 'Pre palivo "Iné" je potrebné doplniť typ paliva.',
      })
    }
    return z.NEVER
  }),
)

export type ExtendedVehicle = z.infer<typeof extendedVehicle>

export const person = z.discriminatedUnion('legalForm', [
  z.object({
    legalForm: z.literal(legalForms.PRIVATE_PERSON),
    zipCode: common.person.zipCode,
    city: common.person.city,
    birthday: common.person.birthday,
    bornNumber: z.optional(common.person.bornNumber),
    ztp: common.person.ztp,
    companyNumber: types.undefinedOptional(),
    companyName: types.undefinedOptional(),
  }),
  z.object({
    legalForm: z.literal(legalForms.COMPANY),
    zipCode: common.person.zipCode,
    city: common.person.city,
    birthday: types.undefinedOptional(),
    bornNumber: types.undefinedOptional(),
    ztp: z.optional(z.literal(false)),
    companyNumber: common.person.companyNumber,
    companyName: z.optional(common.person.companyName),
  }),
  z.object({
    legalForm: z.literal(legalForms.SELF_EMPLOYED),
    zipCode: common.person.zipCode,
    city: common.person.city,
    birthday: types.undefinedOptional(),
    bornNumber: z.optional(common.person.bornNumber),
    ztp: z.optional(z.literal(false)),
    companyNumber: common.person.companyNumber,
    companyName: z.optional(common.person.companyName),
  }),
]).check(
  z.refine(refine.bornNumberMatchesBirthday, refine.bornNumberRefinement),
)

export type Person = z.infer<typeof person>

export const damageClaims = z.object({
  didHavePZP: types.boolean(),
  claims12: common.damageClaimsCount,
  claims24: common.damageClaimsCount,
  claims36: common.damageClaimsCount,
  claims48: common.damageClaimsCount,
  claims60: common.damageClaimsCount,
  claims72: common.damageClaimsCount,
  claims84: common.damageClaimsCount,
  claims96: common.damageClaimsCount,
  claims108: common.damageClaimsCount,
  claims120: common.damageClaimsCount,
})

export const claimsVariants = types.createEnum([
  'claims12', 'claims24', 'claims36', 'claims48', 'claims60', 'claims72', 'claims84', 'claims96', 'claims108',
  'claims120',
])

export type DamageClaims = z.infer<typeof damageClaims>

const uniqaContractNumber = () => z.string().check(
  z.trim(),
  z.minLength(9),
  z.maxLength(10),
  z.regex(/^[0-9]+$/),
)

export const discounts = z.discriminatedUnion('legalForm', [
  z.object({
    legalForm: z.literal(legalForms.PRIVATE_PERSON),
    csobOtherContractsDiscount: z.optional(types.boolean()),
    csobEmergencyBrakingSystem: z.optional(types.boolean()),
    csobParkingAssistantSensors: z.optional(types.boolean()),
    unionUzpDiscount: z.optional(unionV2Discounts),
    uniqaOtherContractNumber: z.optional(uniqaContractNumber()),
  }),
  z.object({
    legalForm: z.literal(legalForms.COMPANY),
    csobOtherContractsDiscount: z.optional(types.boolean()),
    csobEmergencyBrakingSystem: z.optional(types.boolean()),
    csobParkingAssistantSensors: z.optional(types.boolean()),
    unionUzpDiscount: types.undefinedOptional(),
    uniqaOtherContractNumber: z.optional(uniqaContractNumber()),
  }),
  z.object({
    legalForm: z.literal(legalForms.SELF_EMPLOYED),
    csobOtherContractsDiscount: z.optional(types.boolean()),
    csobEmergencyBrakingSystem: z.optional(types.boolean()),
    csobParkingAssistantSensors: z.optional(types.boolean()),
    unionUzpDiscount: types.undefinedOptional(),
    uniqaOtherContractNumber: z.optional(uniqaContractNumber()),
  }),
])

export type Discounts = z.infer<typeof discounts>

const calculationExample = {
  licensePlate: 'BA111EX',
  vin: 'AAAAAAAAAA0000000',
  vehicleType: vehicleTypes.VEHICLE_TYPE_CAR_M1,
  brand: 'bmw',
  model: 'rad5',
  weight: 2320,
  volume: 2926,
  power: 159,
  fuel: fuels.FUEL_BENZIN,
  seats: 5,
  productionYear: 2021,
  legalForm: PRIVATE_PERSON,
  zipCode: '93101',
  city: 'Šamorín',
  birthday: '1980-03-24',
  ztp: true,
  didHavePZP: true,
  claims12: 1,
  claims24: 1,
  claims36: 1,
  claims48: 1,
  claims60: 1,
  claims72: 1,
  claims84: 1,
  claims96: 1,
  claims108: 1,
  claims120: 1,
  uzpDiscount: DISCOUNT_UZP,
  csobOtherContractsDiscount: true,
  csobEmergencyBrakingSystem: true,
  csobParkingAssistantSensor: true,
  uniqaCrossSellDiscount: '123456789',
}

export const calculation = z.intersection(
  vehicle,
  z.intersection(
    person,
    z.intersection(
      damageClaims,
      discounts
    ),
  ),
).register(z.globalRegistry, {example: calculationExample})

export type Calculation = z.infer<typeof calculation>

export const extendedCalculation = z.intersection(
  extendedVehicle,
  z.intersection(
    person,
    z.intersection(
      damageClaims,
      discounts,
    ),
  ),
)

export type ExtendedCalculation = z.infer<typeof extendedCalculation>
